import type { MutableRefObject } from 'react';
import { useRef } from 'react';

import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
} from '@/shared/ui/shadcn/field';

import type { RxFormApi } from '../../model/types';
import { applySelectedMedicationToForm } from '../../model/applySelectedMedicationToForm';
import { useMedicationSearchPicker } from '../../model/useMedicationSearchPicker';
import { getFieldState } from '@/shared/lib/forms';
import { MedicationSearchDialog } from './MedicationSearchDialog';

interface Props {
  form: RxFormApi;
  lastAutoDispenseUnitIdRef?: MutableRefObject<string | null>;
}

export function MedicationSelector({ form, lastAutoDispenseUnitIdRef: externalRef }: Props) {
  const internalRef = useRef<string | null>(null);
  const lastAutoDispenseUnitIdRef = externalRef ?? internalRef;
  const {
    isOpen,
    results,
    isSelecting,
    isSearchTextValid,
    searchQuery,
    searchText,
    searchTextError,
    selectError,
    setSearchText,
    selectMedication,
    setIsOpen,
  } = useMedicationSearchPicker({
    onResolve: ({ selected }) => {
      applySelectedMedicationToForm({ form, selected, lastAutoDispenseUnitIdRef });
    },
  });

  return (
    <FieldGroup>
      <form.Field
        name="selectedMedication"
        children={(field) => {
          const { isInvalid, errors } = getFieldState(field.state.meta);
          const current = field.state.value as {
            DisplayName?: string | null;
            DrugName?: string | null;
          } | null;
          const currentLabel = current?.DisplayName?.trim() || current?.DrugName?.trim() || null;
          const helperText =
            searchText.trim().length > 0 && !isSearchTextValid
              ? (searchTextError ?? 'Type at least 3 characters to search medications.')
              : searchQuery.isError
                ? 'Search failed.'
                : null;
          const shouldRenderFieldContent = helperText != null || isInvalid;

          return (
            <Field data-invalid={isInvalid}>
              {shouldRenderFieldContent ? (
                <FieldContent>
                  {helperText ? <FieldDescription>{helperText}</FieldDescription> : null}
                  {isInvalid ? <FieldError errors={errors} /> : null}
                </FieldContent>
              ) : null}
              <div className="flex w-full flex-col gap-2">
                <MedicationSearchDialog
                  open={isOpen}
                  onOpenChange={setIsOpen}
                  searchText={searchText}
                  onSearchTextChange={setSearchText}
                  onCloseResetSearchText={() => setSearchText('')}
                  isSearchTextValid={isSearchTextValid}
                  searchTextError={searchTextError}
                  isSelecting={isSelecting}
                  isSearchPending={searchQuery.isPending}
                  isSearchError={searchQuery.isError}
                  onRetrySearch={() => void searchQuery.refetch()}
                  results={results}
                  onSelectMedication={(row) => void selectMedication(row)}
                  selectError={selectError}
                  triggerLabel={
                    currentLabel ??
                    (searchText.trim() !== '' ? 'Change medication' : 'Select medication')
                  }
                  triggerDisabled={isSelecting}
                  triggerAriaInvalid={isInvalid}
                />
              </div>
            </Field>
          );
        }}
      />
    </FieldGroup>
  );
}
